import { useCallback } from 'react'
import { Bell, Plus } from 'lucide-react'

import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionPrimary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { NoticeCanvasForm } from '@/components/notices/notice-canvas-form'
import { NoticeMarkdown } from '@/components/notices/notice-markdown'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { getPageHeader } from '@/constants'
import { useNotices, type Notice } from '@/hooks/use-notices'
import { useDeclareUiSurface } from '@/lib/ui-surface'

const NOTICE_FIELDS = ['body', 'audiences', 'starts_at', 'ends_at', 'dismissible', 'also_notify']

const AUDIENCE_LABELS: Record<string, string> = {
  candidate: 'Talent',
  employer: 'Employer',
  recruiter: 'Recruiter',
  interviewer: 'Interviewer',
  platform: 'Platform',
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function audienceLabel(audiences: string[]): string {
  if (!audiences.length) return 'Everyone'
  return audiences.map((name) => AUDIENCE_LABELS[name] ?? name).join(', ')
}

function windowLabel(notice: Notice): string {
  const start = formatWhen(notice.starts_at)
  if (!notice.ends_at) return `${start} – until turned off`
  return `${start} – ${formatWhen(notice.ends_at)}`
}

export function NoticesPage() {
  const { data: notices, isLoading, error } = useNotices()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  useDeclareUiSurface({
    intent: 'view',
    entity: 'notice',
    id: '',
    mode: 'page',
  })

  const openForm = useCallback(
    (notice?: Notice) => {
      openCanvas({
        title: notice ? 'Edit notice' : 'New notice',
        surface: {
          intent: notice ? 'edit' : 'create',
          entity: 'notice',
          id: notice?.id ?? '',
          mode: 'canvas',
          fields: NOTICE_FIELDS,
        },
        content: (
          <NoticeCanvasForm
            key={notice?.id ?? 'new-notice'}
            notice={notice ?? null}
            onClose={closeCanvas}
          />
        ),
      })
    },
    [closeCanvas, openCanvas],
  )

  const newNotice = (
    <PageHeaderActionPrimary onClick={() => openForm()}>
      <Plus className="h-4 w-4" aria-hidden />
      New notice
    </PageHeaderActionPrimary>
  )

  const header = getPageHeader(
    '/platform/notices',
    'Notices',
    'A notice people see in the sidebar.',
  )

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={header.title}
          description={header.description}
          icon={<Bell className="h-5 w-5" />}
          actions={<PageHeaderActions primary={newNotice} />}
        />
      </PageHeaderStack>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status">
          Loading notices…
        </p>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {(error as Error).message || 'Could not load notices.'}
        </p>
      ) : null}

      {!isLoading && !error && notices && notices.length === 0 ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">No notices yet.</p>
          <PageHeaderActionPrimary onClick={() => openForm()}>
            <Plus className="h-4 w-4" aria-hidden />
            New notice
          </PageHeaderActionPrimary>
        </div>
      ) : null}

      {!isLoading && !error && notices && notices.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {notices.map((notice) => (
            <li key={notice.id}>
              <button
                type="button"
                onClick={() => openForm(notice)}
                className="w-full rounded-md border border-border bg-background px-4 py-3 text-left hover:bg-muted/40"
              >
                <NoticeMarkdown>{notice.body}</NoticeMarkdown>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {audienceLabel(notice.audiences)}
                  {' · '}
                  {windowLabel(notice)}
                  {' · '}
                  {notice.dismissible ? 'Can be dismissed' : 'Cannot be dismissed'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </PageContainer>
  )
}
